// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react"
import { Box, CircularProgress, Popover } from "@mui/material"
import { Pencil } from "lucide-react"

import { useToast } from "@crm-src/hooks/use-toast"

/**
 * The render-prop API handed to an inline editor's body. The body lives inside
 * the popover and drives the save:
 *   • `commit(run)` — flips the field into the saving state, runs the async
 *     mutation, closes the popover on success, and toasts on failure (the field
 *     stays open so the user can retry).
 *   • `close()` — dismiss without saving (Cancel).
 *   • `saving` — true while a `commit` is in flight (disable inputs / show a
 *     spinner inside the editor body).
 */
export interface InlineEditRenderApi {
  close: () => void
  /** Run the mutation, then CLOSE the popover on success (used by pick-and-commit selects). */
  commit: (run: () => Promise<unknown>) => Promise<void>
  /** Run the mutation but KEEP the popover open (used by auto-save editors that stay open across changes). */
  save: (run: () => Promise<unknown>) => Promise<void>
  saving: boolean
}

interface InlineEditFieldProps {
  /**
   * When false the field renders its read-only `children` verbatim — no click
   * affordance, no popover. This is how non-admins (and any caller that can't
   * mutate the record) get the plain display. Gate this on the same permission
   * the bulk actions use (`usePermissions().isAdmin()`).
   */
  canEdit: boolean
  /** Accessible name for the trigger button (e.g. "Edit salesperson"). */
  ariaLabel: string
  /** The read display (the same markup the page rendered before inline edit). */
  children: ReactNode
  /** Renders the editor body inside the popover. */
  renderEditor: (api: InlineEditRenderApi) => ReactNode
  /**
   * "cell" (default) tucks the pencil inline next to the value for dense table
   * rows; "panel" reserves a little more breathing room for detail-page rows.
   */
  variant?: "cell" | "panel"
  /** Toast description shown when the mutation throws. */
  errorMessage?: string
}

/**
 * Shared shell for every CRM inline-editable field — used identically in list
 * rows and on detail/overview pages so "edit it where you see it" behaves the
 * same everywhere (assign a salesperson, change priority/status, …) without
 * opening the full record modal.
 *
 * It owns the click-to-open affordance, the anchored popover, the saving
 * spinner, and the error toast. The actual input lives in `renderEditor`; the
 * concrete editors (`InlineSelectEditor`, `InlineSalespeopleEditor`) compose
 * this. The popover content is unmounted while closed, so data-fetching editor
 * bodies (e.g. the salesperson selector calling `useUsers`) only run on open
 * and re-initialise from fresh props every time.
 *
 * See docs/crm.md → "Inline editing".
 */
export function InlineEditField({
  canEdit,
  ariaLabel,
  children,
  renderEditor,
  variant = "cell",
  errorMessage = "Failed to save change",
}: InlineEditFieldProps) {
  const { toast } = useToast()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [saving, setSaving] = useState(false)

  if (!canEdit) {
    return <>{children}</>
  }

  const open = (event: MouseEvent<HTMLElement>) => {
    // List rows wire a navigate-on-click; editing a cell must not also open the
    // detail page.
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const close = () => setAnchorEl(null)

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      event.stopPropagation()
      setAnchorEl(event.currentTarget)
    }
  }

  const runMutation = async (run: () => Promise<unknown>, closeOnSuccess: boolean) => {
    setSaving(true)
    try {
      await run()
      if (closeOnSuccess) close()
    } catch (error) {
      console.error("Inline edit save failed:", error)
      toast({ title: "Error", description: errorMessage, variant: "destructive" })
    } finally {
      setSaving(false)
    }
  }

  const commit = (run: () => Promise<unknown>) => runMutation(run, true)
  const save = (run: () => Promise<unknown>) => runMutation(run, false)

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={open}
        onKeyDown={handleKeyDown}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          borderRadius: 1,
          px: variant === "panel" ? 0.75 : 0.5,
          py: 0.25,
          mx: variant === "panel" ? -0.75 : -0.5,
          maxWidth: "100%",
          transition: "background-color 120ms",
          "&:hover": { bgcolor: "action.hover" },
          "&:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: 1 },
          "&:hover .inline-edit-pencil, &:focus-visible .inline-edit-pencil": { opacity: 1 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>{children}</Box>
        {saving ? (
          <CircularProgress size={12} sx={{ flexShrink: 0, color: "text.secondary" }} />
        ) : (
          <Box
            className="inline-edit-pencil"
            component="span"
            aria-hidden
            sx={{ display: "inline-flex", flexShrink: 0, color: "text.secondary", opacity: 0, transition: "opacity 120ms" }}
          >
            <Pencil size={13} />
          </Box>
        )}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 240, maxWidth: 360 } } }}
      >
        {anchorEl ? renderEditor({ close, commit, save, saving }) : null}
      </Popover>
    </>
  )
}

export default InlineEditField
